import React, {useEffect, useRef} from "react";
import ReactQuill, {Quill} from "react-quill";
import {sanitizeAllowedHtmlForSubmit} from "./EditorUtil";

export const QuillEditor = React.memo(({keyName, value, readOnly, onChange, isEditor}: any) => {
    const quillRef = useRef<ReactQuill | null>(null);
    const formats = [
        "bold", "italic", "underline", "list", "bullet"
    ];
    const modules = {
        toolbar: !readOnly && isEditor
            ? [
                ["bold", "italic", "underline"], // Text formatting options
                [{list: "ordered"}, {list: "bullet"}], // Ordered and unordered lists
            ]
            : false, // Disable toolbar in read-only mode
        clipboard: {
            matchVisual: false,
        },
    };
    useEffect(
        () => {
            const toolbarElements = Array.from(document.querySelectorAll('.ql-toolbar')); // Convert NodeList to an array
            toolbarElements.forEach((toolbarElement) => {
                toolbarElement.addEventListener('mousedown', (e) => {
                    e.preventDefault();
                });
            });
            if (quillRef.current) {
                const editor = quillRef.current.getEditor();
                const Delta = Quill.import("delta");
                const htmlLikeTagRegex = /<\/?[a-z][^>]*>/i;

                const handlePaste = (event: ClipboardEvent) => {
                    if (readOnly || !event.clipboardData) {
                        return;
                    }

                    const pastedHtml = event.clipboardData.getData("text/html");
                    const pastedText = event.clipboardData.getData("text/plain");

                    let incomingHtml = "";
                    if (pastedHtml && htmlLikeTagRegex.test(pastedHtml)) {
                        incomingHtml = pastedHtml;
                    } else if (pastedText && htmlLikeTagRegex.test(pastedText)) {
                        incomingHtml = pastedText;
                    }

                    if (!incomingHtml) {
                        return;
                    }

                    event.preventDefault();
                    const sanitizedHtml = sanitizeAllowedHtmlForSubmit(incomingHtml);
                    const selection = editor.getSelection(true);
                    const insertIndex = selection ? selection.index : editor.getLength();
                    editor.clipboard.dangerouslyPasteHTML(insertIndex, sanitizedHtml, "user");
                };

                editor.root.addEventListener("paste", handlePaste, true);

                // Restrict pasted formatting to only the editor-supported subset.
                editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node: any, delta: any) => {
                    let newDelta = new Delta();
                    delta.ops.forEach((op: any) => {
                        if (op.insert && typeof op.insert === 'string') {
                            // Remove emoji while preserving legal symbols like ©, ®, ™, ℠.
                            const cleanText = op.insert
                            .replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|\uD83E[\uDD10-\uDDFF])/g, '');

                            if (htmlLikeTagRegex.test(cleanText)) {
                                const sanitizedHtml = sanitizeAllowedHtmlForSubmit(cleanText);
                                const convertedDelta = editor.clipboard.convert(sanitizedHtml);
                                newDelta = newDelta.concat(convertedDelta);
                                return;
                            }

                            const sourceAttributes = (op.attributes || {}) as any;
                            const allowedAttributes: any = {};

                            if (sourceAttributes.bold) {
                                allowedAttributes.bold = true;
                            }
                            if (sourceAttributes.italic) {
                                allowedAttributes.italic = true;
                            }
                            if (sourceAttributes.underline) {
                                allowedAttributes.underline = true;
                            }
                            if (sourceAttributes.list === "ordered" || sourceAttributes.list === "bullet") {
                                allowedAttributes.list = sourceAttributes.list;
                            }

                            newDelta.insert(cleanText, allowedAttributes);
                        }
                    });
                    return newDelta;
                });
                // Block <img>, <audio>, <video> tags
                editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
                    if (["IMG", "AUDIO", "VIDEO"].includes(node.nodeName)) {
                        return new Delta();
                    }
                    return delta;
                });

                // Block base64 images
                editor.clipboard.addMatcher("IMG", () => {
                    return new (Quill.import("delta"))();
                });

                (editor as any).__customPasteHandler = handlePaste;
            }
            return () => {
                // Clean up event listeners for all toolbars
                toolbarElements.forEach((toolbarElement) => {
                    toolbarElement.removeEventListener('mousedown', (e) => {
                        e.preventDefault();
                    });
                });

                if (quillRef.current) {
                    const editor = quillRef.current.getEditor();
                    const registeredPasteHandler = (editor as any).__customPasteHandler;
                    if (registeredPasteHandler) {
                        editor.root.removeEventListener("paste", registeredPasteHandler, true);
                        delete (editor as any).__customPasteHandler;
                    }
                }
            };
        },
        [readOnly]
    );
    const handleKeyDown = (e: KeyboardEvent) => {
        if (readOnly) {
            const isAllowedCombo =
                (e.ctrlKey || e.metaKey) && ['a', 'c', 'v'].includes(e.key.toLowerCase());
            if (!isAllowedCombo) {
                e.preventDefault();
            }
        } else if (readOnly) {
            e.preventDefault();
        }
    };

    return (

        <ReactQuill
            ref={quillRef}
            className={`custome-quill-modal ${(readOnly) ? "quill-disabled" : "show-toolbar"}`}
            modules={modules}
            formats={formats}
            value={value}
            onChange={(html) => onChange(keyName, html)}
            onKeyDown={handleKeyDown}
        />

    );
});



// WEBPACK FOOTER //
// ./src/components/Table/components/twoLanguage/QuilEditor.tsx